/**
 * Route Map Component
 *
 * Shows a map with pickup and delivery markers using geocoded coordinates.
 * Web: Google Maps JS API with markers and polyline (no Directions/Embed API needed).
 * Native: react-native-maps with geocoded markers.
 * "Open in Maps" button launches Google Maps for turn-by-turn navigation.
 */
import React, { useEffect, useRef, useState } from 'react';
import { View, Text, StyleSheet, Platform, ActivityIndicator, TouchableOpacity, Linking, Image } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { RD, radii, spacing } from '../theme/colors';
import { loadGoogleMapsScript } from '../utils/googleMaps';

const GOOGLE_API_KEY = 'AIzaSyBWssHGC4J0PqxNbWza5KAXsF5V_g-q0Xg';

interface RouteInfo {
  distanceMiles: number;
  durationMinutes: number;
}

interface RouteMapProps {
  pickupAddress: string;
  deliveryAddress: string;
  driverLocation?: { latitude: number; longitude: number } | null;
  activePhase?: 'pickup' | 'delivery';
  height?: number;
  onRouteInfo?: (info: RouteInfo) => void;
}

interface LatLng {
  lat: number;
  lng: number;
}

/** Geocode an address via Google Geocoding REST API */
async function geocodeAddress(address: string): Promise<LatLng | null> {
  try {
    const encoded = encodeURIComponent(address);
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encoded}&key=${GOOGLE_API_KEY}`;
    const res = await fetch(url);
    const data = await res.json();
    if (data.results?.[0]?.geometry?.location) {
      return data.results[0].geometry.location;
    }
  } catch {}
  return null;
}

/** Open address in native maps app for turn-by-turn directions */
function openInMaps(destinationAddress: string) {
  const destination = encodeURIComponent(destinationAddress);

  if (Platform.OS === 'web') {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${destination}&travelmode=driving`, '_blank');
  } else if (Platform.OS === 'ios') {
    const googleUrl = `comgooglemaps://?daddr=${destination}&directionsmode=driving`;
    const appleUrl = `http://maps.apple.com/?daddr=${destination}&dirflg=d`;
    Linking.canOpenURL('comgooglemaps://').then(supported => {
      Linking.openURL(supported ? googleUrl : appleUrl);
    });
  } else {
    Linking.openURL(`https://www.google.com/maps/dir/?api=1&destination=${destination}&travelmode=driving`);
  }
}

// ─── Web Implementation ───────────────────────────────────────
// Uses Google Maps JS API with geocoded markers

function RouteMapWeb({ pickupAddress, deliveryAddress, driverLocation, activePhase, height = 220, onRouteInfo }: RouteMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const polylineRef = useRef<any>(null);
  const driverMarkerRef = useRef<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !pickupAddress || !deliveryAddress) return;
    let cancelled = false;

    async function init() {
      const [pickupCoord, deliveryCoord] = await Promise.all([
        geocodeAddress(pickupAddress),
        geocodeAddress(deliveryAddress),
      ]);

      if (cancelled || !pickupCoord || !deliveryCoord) {
        if (!cancelled) { setError(true); setLoading(false); }
        return;
      }

      loadGoogleMapsScript(() => {
        if (cancelled || !mapRef.current) return;
        const google = (window as any).google;

        const bounds = new google.maps.LatLngBounds();
        bounds.extend(pickupCoord);
        bounds.extend(deliveryCoord);

        if (!mapInstanceRef.current) {
          mapInstanceRef.current = new google.maps.Map(mapRef.current, {
            center: bounds.getCenter(),
            zoom: 12,
            disableDefaultUI: true,
            zoomControl: true,
            styles: mapStyles,
          });
        }

        mapInstanceRef.current.fitBounds(bounds, { top: 40, bottom: 40, left: 40, right: 40 });

        // Clear old markers
        markersRef.current.forEach(m => m.setMap(null));
        markersRef.current = [];
        if (polylineRef.current) polylineRef.current.setMap(null);

        // Try Directions for real route line
        try {
          const directionsService = new google.maps.DirectionsService();
          directionsService.route(
            {
              origin: pickupCoord,
              destination: deliveryCoord,
              travelMode: google.maps.TravelMode.DRIVING,
            },
            (result: any, status: string) => {
              if (cancelled) return;
              if (status === 'OK' && result?.routes?.[0]) {
                const path = result.routes[0].overview_path;
                polylineRef.current = new google.maps.Polyline({
                  path,
                  strokeColor: '#4285F4',
                  strokeWeight: 5,
                  strokeOpacity: 0.8,
                  map: mapInstanceRef.current,
                });
                // Extract route info
                const leg = result.routes[0].legs?.[0];
                if (leg && onRouteInfo) {
                  onRouteInfo({
                    distanceMiles: Math.round((leg.distance?.value || 0) / 1609.34),
                    durationMinutes: Math.ceil((leg.duration?.value || 0) / 60),
                  });
                }
              } else {
                // Fallback: straight line
                polylineRef.current = new google.maps.Polyline({
                  path: [pickupCoord, deliveryCoord],
                  strokeColor: RD.maroon,
                  strokeWeight: 3,
                  strokeOpacity: 0.6,
                  icons: [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 3 }, offset: '0', repeat: '15px' }],
                  map: mapInstanceRef.current,
                });
              }
            }
          );
        } catch {
          // Fallback: straight line
          polylineRef.current = new google.maps.Polyline({
            path: [pickupCoord, deliveryCoord],
            strokeColor: RD.maroon,
            strokeWeight: 3,
            strokeOpacity: 0.6,
            icons: [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 3 }, offset: '0', repeat: '15px' }],
            map: mapInstanceRef.current,
          });
        }

        // Pickup marker (sunflower)
        const pickupMarker = new google.maps.Marker({
          position: pickupCoord,
          map: mapInstanceRef.current,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: RD.amber,
            fillOpacity: 1,
            strokeColor: '#fff',
            strokeWeight: 3,
          },
          label: { text: 'P', color: '#fff', fontFamily: 'Arial', fontSize: '10' },
        });
        markersRef.current.push(pickupMarker);

        // Delivery marker (black)
        const deliveryMarker = new google.maps.Marker({
          position: deliveryCoord,
          map: mapInstanceRef.current,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: RD.ink,
            fillOpacity: 1,
            strokeColor: '#fff',
            strokeWeight: 3,
          },
          label: { text: 'D', color: '#fff', fontFamily: 'Arial', fontSize: '10' },
        });
        markersRef.current.push(deliveryMarker);

        setLoading(false);
      });
    }

    init();
    return () => { cancelled = true; };
  }, [pickupAddress, deliveryAddress]);

  // Update driver marker
  useEffect(() => {
    if (!driverLocation || !mapInstanceRef.current || typeof window === 'undefined') return;
    const google = (window as any).google;
    if (!google?.maps) return;

    if (!driverMarkerRef.current) {
      driverMarkerRef.current = new google.maps.Marker({
        position: { lat: driverLocation.latitude, lng: driverLocation.longitude },
        map: mapInstanceRef.current,
        icon: {
          path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
          scale: 6,
          fillColor: RD.info,
          fillOpacity: 1,
          strokeColor: '#fff',
          strokeWeight: 2,
        },
        zIndex: 999,
      });
    } else {
      driverMarkerRef.current.setPosition({ lat: driverLocation.latitude, lng: driverLocation.longitude });
    }
  }, [driverLocation]);

  if (error) {
    return (
      <View style={[styles.container, styles.errorWrap, { height }]}>
        <Text style={styles.errorText}>Map unavailable for this route</Text>
      </View>
    );
  }

  return (
    <View style={styles.outerWrap}>
      <View style={[styles.container, { height }]}>
        {loading && (
          <View style={[StyleSheet.absoluteFillObject, styles.loadingOverlay]}>
            <ActivityIndicator size="small" color={RD.ink} />
          </View>
        )}
        <div ref={mapRef} style={{ width: '100%', height: '100%', borderRadius: 16 }} />
      </View>

      {/* Open in Maps button */}
      <View style={styles.routeBar}>
        <View style={styles.routeInfoRow}>
          <View style={styles.routeChip}>
            <View style={[styles.legendDot, { backgroundColor: RD.amber }]} />
            <Text style={styles.routeChipText}>Pickup</Text>
          </View>
          <View style={styles.routeChip}>
            <View style={[styles.legendDot, { backgroundColor: RD.ink }]} />
            <Text style={styles.routeChipText}>Delivery</Text>
          </View>
        </View>
        <TouchableOpacity
          style={styles.openMapsBtn}
          onPress={() => openInMaps(activePhase === 'delivery' ? deliveryAddress : pickupAddress)}
          activeOpacity={0.7}
        >
          <Ionicons name="navigate" size={15} color={'#fff'} />
          <Text style={styles.openMapsBtnText}>Navigate</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

/** Compact bar showing distance + drive time from Google Directions */
export function RouteInfoBar({ distanceMiles, durationMinutes }: RouteInfo) {
  const fmtTime = durationMinutes < 60
    ? `${durationMinutes} min`
    : `${Math.floor(durationMinutes / 60)} h ${durationMinutes % 60 > 0 ? `${durationMinutes % 60} min` : ''}`.trim();
  return (
    <View style={styles.routeInfoBar}>
      <View style={styles.routeInfoItem}>
        <Ionicons name="speedometer-outline" size={15} color={RD.textMuted} />
        <Text style={styles.routeInfoValue}>{distanceMiles} mi</Text>
      </View>
      <View style={styles.routeInfoDivider} />
      <View style={styles.routeInfoItem}>
        <Ionicons name="time-outline" size={15} color={RD.textMuted} />
        <Text style={styles.routeInfoValue}>{fmtTime} drive</Text>
      </View>
    </View>
  );
}

// ─── Native Implementation ────────────────────────────────────

function RouteMapNative({ pickupAddress, deliveryAddress, driverLocation, activePhase, height = 220 }: RouteMapProps) {
  const [pickupCoord, setPickupCoord] = useState<{ latitude: number; longitude: number } | null>(null);
  const [deliveryCoord, setDeliveryCoord] = useState<{ latitude: number; longitude: number } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!pickupAddress || !deliveryAddress) return;
    (async () => {
      const [p, d] = await Promise.all([
        geocodeAddress(pickupAddress),
        geocodeAddress(deliveryAddress),
      ]);
      if (p) setPickupCoord({ latitude: p.lat, longitude: p.lng });
      if (d) setDeliveryCoord({ latitude: d.lat, longitude: d.lng });
      setLoading(false);
    })();
  }, [pickupAddress, deliveryAddress]);

  if (loading) {
    return (
      <View style={[styles.container, styles.loadingOverlay, { height }]}>
        <ActivityIndicator size="small" color={RD.ink} />
      </View>
    );
  }

  if (!pickupCoord || !deliveryCoord) {
    return (
      <View style={[styles.container, styles.errorWrap, { height }]}>
        <Text style={styles.errorText}>Map unavailable for this route</Text>
      </View>
    );
  }

  const MapView = require('react-native-maps').default;
  const { Marker, Polyline } = require('react-native-maps');

  const midLat = (pickupCoord.latitude + deliveryCoord.latitude) / 2;
  const midLng = (pickupCoord.longitude + deliveryCoord.longitude) / 2;
  const latDelta = Math.abs(pickupCoord.latitude - deliveryCoord.latitude) * 1.5 + 0.02;
  const lngDelta = Math.abs(pickupCoord.longitude - deliveryCoord.longitude) * 1.5 + 0.02;

  return (
    <View style={styles.outerWrap}>
      <View style={[styles.container, { height }]}>
        <MapView
          style={StyleSheet.absoluteFillObject}
          region={{ latitude: midLat, longitude: midLng, latitudeDelta: latDelta, longitudeDelta: lngDelta }}
        >
          <Polyline
            coordinates={[pickupCoord, deliveryCoord]}
            strokeColor={RD.maroon}
            strokeWidth={3}
            lineDashPattern={[8, 4]}
          />
          <Marker coordinate={pickupCoord} title="Pickup" pinColor={RD.amber} />
          <Marker coordinate={deliveryCoord} title="Delivery" pinColor={RD.ink} />
          {driverLocation && <Marker coordinate={driverLocation} title="You" pinColor={RD.info} />}
        </MapView>
      </View>

      {/* Open in Maps */}
      <View style={styles.routeBar}>
        <View style={styles.routeChip}>
          <Ionicons name="navigate-outline" size={14} color={RD.textSec} />
          <Text style={styles.routeChipText}>Route</Text>
        </View>
        <TouchableOpacity
          style={styles.openMapsBtn}
          onPress={() => openInMaps(activePhase === 'delivery' ? deliveryAddress : pickupAddress)}
          activeOpacity={0.7}
        >
          <Ionicons name="navigate" size={15} color={'#fff'} />
          <Text style={styles.openMapsBtnText}>Navigate</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

// ─── Main Export ───────────────────────────────────────────────

export function RouteMap(props: RouteMapProps) {
  if (!props.pickupAddress || !props.deliveryAddress) return null;
  if (Platform.OS === 'web') return <RouteMapWeb {...props} />;
  return <RouteMapNative {...props} />;
}

// ─── Styles ───────────────────────────────────────────────────

const mapStyles = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', stylers: [{ color: '#d4e4f7' }] },
  { featureType: 'landscape', stylers: [{ color: '#f5f0eb' }] },
];

const styles = StyleSheet.create({
  outerWrap: {},
  container: {
    borderRadius: 16,
    overflow: 'hidden',
    backgroundColor: RD.g07,
  },
  loadingOverlay: {
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  errorWrap: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: RD.g05,
  },
  errorText: {
    fontSize: 13,
    color: RD.textMuted,
    fontWeight: '500',
  },
  routeBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    paddingHorizontal: 4,
  },
  routeInfoRow: {
    flexDirection: 'row',
    gap: 8,
  },
  routeChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: RD.g05,
    borderRadius: 20,
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  routeChipText: {
    fontSize: 12,
    fontWeight: '600',
    color: RD.textSec,
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  openMapsBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: RD.ink,
    borderRadius: 20,
    paddingHorizontal: 14,
    paddingVertical: 8,
  },
  openMapsBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#fff',
  },
  routeInfoBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: RD.g05,
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 16,
    marginTop: 8,
    gap: 16,
  },
  routeInfoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  routeInfoValue: {
    fontSize: 14,
    fontWeight: '700',
    color: RD.text,
  },
  routeInfoDivider: {
    width: 1,
    height: 16,
    backgroundColor: RD.borderStrong,
  },
});
